import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, LogIn } from "lucide-react";
import api from "@/services/api";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  useEffect(() => {
    if (user && !authLoading) {
      if (user.perfil_comercial === "embajador_digital" && localStorage.getItem("embajador")) {
        navigate("/embajador/dashboard");
        return;
      }
      if (user.rol === "distribuidor") {
        navigate("/mayorista");
        return;
      }
      navigate(user.rol === "admin" || user.rol === "supervisor" ? "/admin" : "/vendedor");
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { usuario, embajador } = await login(form.email, form.password);
      toast.success(`¡Bienvenido, ${usuario.nombre}!`);
      let redirectPath = usuario.rol === "admin" || usuario.rol === "supervisor" ? "/admin" : "/vendedor";
      if (usuario.rol === "distribuidor") {
        redirectPath = "/mayorista";
      }
      if (usuario.perfil_comercial === "embajador_digital" && embajador) {
        redirectPath = "/embajador/dashboard";
      }
      // Use window.location for reliable redirect
      setTimeout(() => {
        window.location.href = redirectPath;
      }, 500);
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Error al iniciar sesión");
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem("google_proposito", "login");
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const response = await api.post("/seed");
      toast.success("Datos iniciales creados");
      console.log("Seed response:", response.data);
    } catch (error) {
      if (error.response?.data?.message === "Datos ya existentes") {
        toast.info("Los datos ya existen");
      } else {
        toast.error("Error al crear datos");
      }
    } finally {
      setSeeding(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-torta-cream">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-torta-cream to-torta-sand p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src="/logo-torta-cuajada.png"
              alt="Baruj Shefa"
              className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border-4 border-torta-brown"
            />
            <h1 className="font-display text-3xl text-torta-dark">Baruj Shefa SAS</h1>
            <p className="text-torta-muted text-sm mt-1">Sistema de Gestión - Torta de Cuajada</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2 text-torta-dark">
                <Mail className="w-4 h-4 text-torta-brown" />
                Correo electrónico
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="tu@email.com"
                className="h-12 border-torta-sand focus:border-torta-brown focus:ring-torta-brown/20"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2 text-torta-dark">
                <Lock className="w-4 h-4 text-torta-brown" />
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                className="h-12 border-torta-sand focus:border-torta-brown focus:ring-torta-brown/20"
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-torta-brown hover:bg-torta-brown/90 text-white"
              data-testid="login-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Ingresando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="w-5 h-5" />
                  Ingresar al Sistema
                </span>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative flex items-center justify-center my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-torta-sand"></div>
            </div>
            <span className="relative bg-white px-4 text-sm text-torta-muted">o</span>
          </div>

          {/* Google Login */}
          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleLogin}
            className="w-full h-12 border-2 border-torta-sand hover:bg-torta-cream/50 rounded-xl flex items-center justify-center gap-3"
            data-testid="login-google-btn"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span className="font-medium text-torta-dark">Continuar con Google</span>
          </Button>

          {/* Back link */}
          <div className="mt-6 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-torta-brown hover:text-torta-dark transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver a la página principal
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
